/**
 * Phase 66 Plan 03 — Sparkline + history metric endpoints.
 *
 * The backend `/api/v1/metrics/sparkline` endpoint is NOT YET implemented
 * (deferred follow-up — see CONTEXT.md "Deferred ideas → Sparkline backend
 * endpoint"). Plan 03 ships the client side with a 404-graceful fallback
 * that:
 *   - returns a zero-filled placeholder series of `days` points
 *   - emits ONE console.warn per metric per page load (C-5 contract)
 *
 * 5xx errors propagate normally as `ApiError` so QA/Marco see them as
 * inline card errors (independent-failure contract).
 *
 * History endpoints (deposits-24h + online-7d) are queried via the same
 * pattern; if they 404 they return zero-fill too — Plan 03 ships
 * the chart shell now and Plan 67+ wires the real backend.
 *
 * @see plan 66-03 Task 1 step 4 — Sparkline contract
 * @see RESEARCH.md §Pitfall 7 — ResponsiveContainer height=0 mitigation
 */

import { apiGet } from "./client";
import type { ApiError } from "./client";
import type { DepositPoint, OnlinePoint } from "@/types/api";

export type SparklineMetric =
  | "machines_total"
  | "machines_online"
  | "provisioning_pending"
  | "vpn_peers"
  | "deposits_24h"
  | "online_history";

export interface SparkPoint {
  /** ISO date string (yyyy-mm-dd or full ISO). */
  ts: string;
  /** Value at the bucket. */
  v: number;
}

/**
 * Module-level guard: emit ONE console.warn per metric per page load
 * when the sparkline endpoint returns 404. Cleared by full-page reload
 * (module re-evaluation). Test contract: `__resetSparklineWarn` exposed
 * for unit tests below.
 */
const sparklineWarnedMetrics = new Set<SparklineMetric>();

/** Test-only: reset the warn-once guard between Vitest cases. */
export function __resetSparklineWarn(): void {
  sparklineWarnedMetrics.clear();
}

function zeroFillSeries(days: number): SparkPoint[] {
  const out: SparkPoint[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    out.push({ ts: d.toISOString().slice(0, 10), v: 0 });
  }
  return out;
}

/**
 * Fetch a sparkline series for the given metric. On 404, return a
 * zero-filled placeholder and warn once per metric per page load.
 * Other errors propagate.
 */
export async function getSparkline(
  metric: SparklineMetric,
  days = 7,
): Promise<SparkPoint[]> {
  try {
    return await apiGet<SparkPoint[]>(
      `/metrics/sparkline?metric=${encodeURIComponent(metric)}&days=${days}`,
    );
  } catch (err) {
    const apiErr = err as ApiError;
    if (apiErr?.status === 404) {
      if (!sparklineWarnedMetrics.has(metric)) {
        sparklineWarnedMetrics.add(metric);
        // eslint-disable-next-line no-console -- intentional dev/QA visibility (C-5)
        console.warn(
          `[Phase 66] Sparkline data unavailable for metric "${metric}" — backend endpoint /api/v1/metrics/sparkline pending. Rendering zero-filled placeholder.`,
        );
      }
      return zeroFillSeries(days);
    }
    throw err;
  }
}

/**
 * Deposits aggregated per hour for the last 24h. Same 404 fallback
 * pattern as sparkline: missing endpoint → zero-fill, single warn.
 */
export async function getDepositsHistory(): Promise<DepositPoint[]> {
  try {
    return await apiGet<DepositPoint[]>("/metrics/deposits?window=24h");
  } catch (err) {
    const apiErr = err as ApiError;
    if (apiErr?.status === 404) {
      if (!sparklineWarnedMetrics.has("deposits_24h")) {
        sparklineWarnedMetrics.add("deposits_24h");
        // eslint-disable-next-line no-console -- intentional dev/QA visibility (C-5)
        console.warn(
          `[Phase 66] Deposits history unavailable — backend endpoint /api/v1/metrics/deposits pending. Rendering zero-filled placeholder.`,
        );
      }
      const out: DepositPoint[] = [];
      for (let i = 23; i >= 0; i--) {
        const d = new Date();
        d.setHours(d.getHours() - i, 0, 0, 0);
        out.push({ ts: d.toISOString(), count: 0 });
      }
      return out;
    }
    throw err;
  }
}

/** Online kiosks per day for the last 7 days. Same 404 fallback. */
export async function getOnlineHistory(): Promise<OnlinePoint[]> {
  try {
    return await apiGet<OnlinePoint[]>("/metrics/online?days=7");
  } catch (err) {
    const apiErr = err as ApiError;
    if (apiErr?.status === 404) {
      if (!sparklineWarnedMetrics.has("online_history")) {
        sparklineWarnedMetrics.add("online_history");
        // eslint-disable-next-line no-console -- intentional dev/QA visibility (C-5)
        console.warn(
          `[Phase 66] Online history unavailable — backend endpoint /api/v1/metrics/online pending. Rendering zero-filled placeholder.`,
        );
      }
      const out: OnlinePoint[] = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        d.setHours(0, 0, 0, 0);
        out.push({ ts: d.toISOString(), online: 0, total: 0 });
      }
      return out;
    }
    throw err;
  }
}
